// src/components/empleado/FichajeBloque.jsx
import { useEffect, useState } from "react";
import { parseISO, fmtHoraISO } from "../../utils/fecha";
import { formatDuracionMin } from "../../utils/tiempos";

// Normaliza los distintos formatos que puede traer el backend
function getISO(v) {
  if (!v) return null;
  if (typeof v === "string") return v;               // "2025-09-12T08:00:00+02:00"
  if (typeof v === "number") return new Date(v).toISOString();
  if (typeof v === "object") {
    return v.timestamp || v.ts || v.iso || null;     // { timestamp } / { ts } / { iso }
  }
  return null;
}

function safeParse(iso) {
  if (!iso) return null;
  const d = parseISO(iso);
  return d && !isNaN(d) ? d : null;
}

// Extrae un posible label de localización de varios nombres de campo (tolerante)
function pickLabel(obj) {
  if (!obj || typeof obj !== "object") return null;
  return (
    obj.loc_label ||
    obj.locLabel ||
    obj.label ||
    (obj.loc && (obj.loc.label || obj.loc.name)) ||
    null
  );
}

function formatHora(iso) {
  return iso ? (fmtHoraISO(iso) || "—") : "—";
}

function formatDuracion(seg) {
  if (seg == null) return "—";
  const mins = Math.max(0, Math.floor(seg / 60));
  return formatDuracionMin(mins);
}

// Chip compacto y truncable para ubicaciones (evita romper diseño en móvil)
function LocChip({ label }) {
  if (!label) return null;
  return (
    <span
      className="inline-flex items-center gap-1 max-w-full px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-[11px] leading-none"
      title={label}
    >
      <span aria-hidden>📍</span>
      <span className="truncate">{label}</span>
    </span>
  );
}

// Chip pequeño para “manual”
function ManualChip() {
  return (
    <span className="text-[11px] leading-none px-2 py-1 rounded-full bg-blue-50 text-blue-600">
      ✍️ manual
    </span>
  );
}

export default function FichajeBloque({ entrada, salida, duracion, anomalia }) {
  const entradaISO = getISO(entrada);
  const salidaISO  = getISO(salida);

  const tEntrada = safeParse(entradaISO);
  const tSalida  = safeParse(salidaISO);

  const entradaLabel = pickLabel(entrada);
  const salidaLabel  = pickLabel(salida);

  const [duracionActual, setDuracionActual] = useState(
    typeof duracion === "number" ? duracion : 0
  );

  const entradaManual = typeof entrada === "object" ? !!entrada?.is_manual : false;
  const salidaManual  = typeof salida  === "object" ? !!salida?.is_manual  : false;

  // Contador en vivo cuando hay entrada pero no hay salida
  useEffect(() => {
    if (tEntrada && !tSalida) {
      const inicioMs = tEntrada.getTime();
      const tick = () => {
        const diff = Math.max(0, Math.floor((Date.now() - inicioMs) / 1000));
        setDuracionActual(diff);
      };
      tick(); // cálculo inmediato
      const id = setInterval(tick, 1000);
      return () => clearInterval(id);
    } else {
      setDuracionActual(typeof duracion === "number" ? duracion : 0);
    }
  }, [entradaISO, salidaISO, tEntrada, tSalida, duracion]);

  return (
    <div
      className={`rounded-2xl p-4 sm:p-5 border transition-all duration-200 shadow-sm hover:shadow-md ${
        anomalia ? "bg-red-100 border-red-300 text-red-900" : "bg-white border-gray-200 text-gray-800"
      }`}
    >
      {/* ENTRADA */}
      {tEntrada ? (
        <p className="text-sm sm:text-[15px] font-medium text-green-700 flex items-center flex-wrap gap-x-2 gap-y-1">
          <span>🟢 Entrada:</span>
          <span>{formatHora(entradaISO)}</span>
          {entradaManual && <ManualChip />}
          <LocChip label={entradaLabel} />
        </p>
      ) : (
        <p className="text-sm text-red-700">⚠️ Falta la entrada</p>
      )}

      {/* SALIDA */}
      {tSalida ? (
        <p className="text-sm sm:text-[15px] font-medium text-red-600 flex items-center flex-wrap gap-x-2 gap-y-1 mt-1">
          <span>🔴 Salida:</span>
          <span>{formatHora(salidaISO)}</span>
          {salidaManual && <ManualChip />}
          <LocChip label={salidaLabel} />
        </p>
      ) : (
        <p className="text-sm text-yellow-600 mt-1">🕓 Aún sin salida</p>
      )}

      {/* Anomalía */}
      {anomalia && (
        <p className="text-xs italic mt-2 text-red-800">⚠️ {anomalia}</p>
      )}

      {/* Duración */}
      <p className="text-xs text-gray-500 italic mt-2">
        ⏱️ Tiempo efectivo (tramo): {formatDuracion(duracionActual)}
      </p>
    </div>
  );
}
